class AutoScrollGallery extends HTMLElement {
  constructor() {
    super();
    this.splide = null;
    this.fullscreenSplide = null;
    this.speed = Number.parseInt(this.dataset.speed) || 1;
    this.isMobile = window.innerWidth < 1024;
    this.dialog = null;
    this._lastTrigger = null; // Track what triggered the modal
    this._keyboardOpen = false;

    // FPS diagnostics
    this._fpsFrameCount = 0;
    this._fpsLastTime = performance.now();
    this._fpsInterval = null;
    this._fpsRafId = null;
    this._fpsFrameTimes = [];

    // Time-based scroll state
    this._timeScrollRafId = null;
    this._timeScrollLastTimestamp = null;
    this._timeScrollPaused = false;
  }

  connectedCallback() {
    this.dialog = this.querySelector('.fullscreen-carousel-dialog');

    this.setupDialogEvents();
    this._startFpsDiagnostics();

    // Add a small delay to ensure DOM is ready
    setTimeout(() => {
      this.waitForSplide();
    }, 100);
  }

  disconnectedCallback() {
    this._stopFpsDiagnostics();
    this._stopTimeBasedScroll();

    if (this.splide) {
      this.splide.destroy();
    }
    if (this.fullscreenSplide) {
      this.fullscreenSplide.destroy();
    }

    // Remove dialog event listeners
    if (this.dialog) {
      const closeButton = this.dialog.querySelector('.fullscreen-carousel-dialog__close');
      if (closeButton) {
        closeButton.removeEventListener('click', this.closeDialog.bind(this));
      }
      this.dialog.removeEventListener('click', this.handleDialogClick.bind(this));
      this.dialog.removeEventListener('close', this.handleDialogClose.bind(this));
    }
  }

  _startFpsDiagnostics() {
    const ua = navigator.userAgent;
    const platform = navigator.platform || navigator.userAgentData?.platform || 'unknown';
    const screenRefresh = window.screen?.refreshRate || 'unknown';
    const dpr = window.devicePixelRatio || 1;

    console.log('[ASG] === Auto-Scroll Gallery Diagnostics ===');
    console.log('[ASG] Platform:', platform);
    console.log('[ASG] User Agent:', ua);
    console.log('[ASG] Screen:', window.screen.width + 'x' + window.screen.height, '@ DPR', dpr);
    console.log('[ASG] Screen refresh rate (if available):', screenRefresh);
    console.log('[ASG] Speed setting:', this.speed, '/ 100');
    console.log('[ASG] Normalized speed (px/frame):', ((this.speed / 100) * 3).toFixed(4));
    console.log('[ASG] prefers-reduced-motion:', window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    console.log('[ASG] Viewport:', window.innerWidth + 'x' + window.innerHeight);
    console.log('[ASG] isMobile:', this.isMobile);
    console.log('[ASG] ==========================================');

    // rAF-based frame counter
    const countFrame = (now) => {
      this._fpsFrameCount++;
      this._fpsFrameTimes.push(now);
      // Keep only last 120 frame times for jitter analysis
      if (this._fpsFrameTimes.length > 120) {
        this._fpsFrameTimes.shift();
      }
      this._fpsRafId = requestAnimationFrame(countFrame);
    };
    this._fpsRafId = requestAnimationFrame(countFrame);

    // Log every 1 second
    this._fpsInterval = setInterval(() => {
      const now = performance.now();
      const elapsed = (now - this._fpsLastTime) / 1000;
      const fps = this._fpsFrameCount / elapsed;

      // Calculate frame time stats from recent frames
      let avgFrameTime = 0;
      let minFrameTime = Infinity;
      let maxFrameTime = 0;
      let jitter = 0;
      const frameTimes = this._fpsFrameTimes;
      const deltas = [];
      for (let i = 1; i < frameTimes.length; i++) {
        const delta = frameTimes[i] - frameTimes[i - 1];
        deltas.push(delta);
      }
      if (deltas.length > 0) {
        avgFrameTime = deltas.reduce((a, b) => a + b, 0) / deltas.length;
        minFrameTime = Math.min(...deltas);
        maxFrameTime = Math.max(...deltas);
        // Jitter = standard deviation of frame times
        const variance = deltas.reduce((sum, d) => sum + Math.pow(d - avgFrameTime, 2), 0) / deltas.length;
        jitter = Math.sqrt(variance);
      }

      // Get scroll state
      let scrollState = this._timeScrollPaused ? 'PAUSED' : 'PLAYING';
      let currentPosition = 'N/A';
      if (this.splide && this.splide.Components && this.splide.Components.Move) {
        currentPosition = this.splide.Components.Move.getPosition().toFixed(1);
      }

      // Target speed is constant regardless of FPS
      const targetPxPerSec = this._timeScrollPxPerSec || 0;

      console.log(
        '[ASG] FPS: %s | Frame: avg %sms, min %sms, max %sms, jitter %sms | Scroll: %s, pos %s | Target speed: %s px/s (time-based)',
        fps.toFixed(1),
        avgFrameTime.toFixed(2),
        minFrameTime === Infinity ? '-' : minFrameTime.toFixed(2),
        maxFrameTime === 0 ? '-' : maxFrameTime.toFixed(2),
        jitter.toFixed(2),
        scrollState,
        currentPosition,
        targetPxPerSec.toFixed(1)
      );

      // Reset counters
      this._fpsFrameCount = 0;
      this._fpsLastTime = now;
    }, 1000);
  }

  _stopFpsDiagnostics() {
    if (this._fpsInterval) {
      clearInterval(this._fpsInterval);
      this._fpsInterval = null;
    }
    if (this._fpsRafId) {
      cancelAnimationFrame(this._fpsRafId);
      this._fpsRafId = null;
    }
  }

  setupDialogEvents() {
    if (!this.dialog) {
      return;
    }
    // Set up click events on slide buttons to open dialog
    const slideButtons = this.querySelectorAll('.auto-scroll-gallery__item-button');
    slideButtons.forEach((button, index) => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this._lastTrigger = button;
        this._keyboardOpen = false;
        this.openDialog(index);
      });
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          e.stopPropagation();
          this._lastTrigger = button;
          this._keyboardOpen = true;
          this.openDialog(index);
        }
      });
    });

    // Set up close button
    const closeButton = this.dialog.querySelector('.fullscreen-carousel-dialog__close');
    if (closeButton) {
      closeButton.addEventListener('click', (e) => this.closeDialog(e));
    } 

    // Add click event to close dialog when clicking anywhere inside it
    this.dialog.addEventListener('click', this.handleDialogClick.bind(this));

    // Handle dialog close event
    this.dialog.addEventListener('close', this.handleDialogClose.bind(this));

    // Prevent pagination clicks from closing the dialog
    const paginationContainer = this.dialog.querySelector('.fullscreen-carousel__pagination');
    if (paginationContainer) {
      paginationContainer.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }
  }

  setupModalArrowNavigation() {
    // Instead of handling arrow keys ourselves, let Splide handle the navigation
    // and we'll just move focus if it's on an inactive indicator
    if (!this.fullscreenSplide) return;

    this.fullscreenSplide.on('moved', (newIndex) => {
      this.moveFocusFromInactiveIndicator();
    });
  }

  moveFocusFromInactiveIndicator() {
    const paginationContainer = this.dialog.querySelector('.fullscreen-carousel__pagination');
    if (!paginationContainer) {
      return;
    }
    
    // Check if any pagination button has focus-visible
    const focusedPaginationButton = paginationContainer.querySelector('.pagination-number:focus-visible');
    
    if (!focusedPaginationButton) {
      return;
    }
    
    // If there's a focus-visible pagination button, move focus to the active one
    const activePaginationButton = paginationContainer.querySelector('.pagination-number.active');
    if (activePaginationButton) {
      activePaginationButton.focus();
    }
  }

  waitForSplide() {
    // Check if Splide and AutoScroll extension are available
    if (typeof Splide !== 'undefined' && this.isSplideAutoScrollLoaded()) {
      this.initSplide();
      return;
    }
    // Set up an interval to check for Splide and AutoScroll
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max
    
    const checkInterval = setInterval(() => {
      attempts++;
      
      if (typeof Splide !== 'undefined' && this.isSplideAutoScrollLoaded()) {
        clearInterval(checkInterval);
        this.initSplide();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        this.initFallback();
      }
    }, 100);
  }

  isSplideAutoScrollLoaded() {
    // For CDN usage, check window.splide.Extensions
    const available = typeof Splide !== 'undefined' && 
           typeof window.splide !== 'undefined' && 
           typeof window.splide.Extensions !== 'undefined';
    
    return available;
  }

  initFallback() {
    // Try to initialize basic Splide without AutoScroll
    if (typeof Splide !== 'undefined') {
      this.initBasicSplide();
    } 
  }

  initBasicSplide() {
    try {
      this.splide = new Splide(this, {
        type: 'loop',
        drag: 'free',
        focus: 0,
        perPage: 'auto',
        perMove: 1,
        gap: 0,
        pagination: false,
        arrows: false,
        keyboard: true,
        drag: true,
      });

      this.splide.mount();
    } catch (error) {
    }
  }

  initSplide() {
    // Check DOM structure
    const track = this.querySelector('.splide__track');
    const list = this.querySelector('.splide__list');
    const slides = this.querySelectorAll('.splide__slide');

    if (!track || !list || slides.length === 0) {
      return;
    }

    // Time-based speed: convert from 1-100 scale to pixels per second
    // At speed=17 (current setting), this yields ~50 px/s which matches the Mac experience
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._timeScrollPxPerSec = (this.speed / 100) * 3 * 60; // Normalize as if 60fps baseline
    if (reducedMotion) {
      this._timeScrollPxPerSec *= 0.5;
    }

    console.log('[ASG] Time-based speed: %s px/s (reduced motion: %s)', this._timeScrollPxPerSec.toFixed(1), reducedMotion);

    try {
      // Calculate clones needed for smooth looping with AutoScroll
      const slideCount = slides.length;
      const calculatedClones = Math.max(slideCount, 6); // Ensure at least 6 clones for smooth looping

      // Initialize main auto-scroll gallery
      // AutoScroll is mounted but immediately paused — we drive scrolling ourselves
      const config = {
        type: 'loop',
        drag: 'free',
        focus: 0,
        perPage: 'auto', // Auto-fit slides based on their width
        perMove: 1,
        gap: 0,
        pagination: false,
        arrows: false,
        clones: calculatedClones, // Dynamically calculated clones for smooth looping
        autoScroll: {
          speed: 0, // Effectively disabled — we handle scrolling
          autoStart: false,
          pauseOnHover: false,
          pauseOnFocus: false,
        },
        // Enable keyboard navigation
        keyboard: true,
        // Allow touch/mouse dragging without snapping
        drag: 'free',
        // Disable snapping behavior for free dragging
        flickPower: 0,
        flickMaxPages: 0,
      };

      this.splide = new Splide(this, config);

      // Mount with AutoScroll extension (needed for loop/clone infrastructure)
      this.splide.mount(window.splide.Extensions);

      // Ensure built-in AutoScroll stays paused
      if (this.splide.Components && this.splide.Components.AutoScroll) {
        this.splide.Components.AutoScroll.pause();
      }

      // Start our time-based scroll loop
      this._startTimeBasedScroll();

      // Resume time-based scroll after dragging ends
      this.splide.on('dragged', () => {
        setTimeout(() => {
          this._resumeTimeBasedScroll();
        }, 100);
      });

      // Also resume after any move operation completes
      this.splide.on('moved', () => {
        if (this._timeScrollPaused) {
          setTimeout(() => {
            this._resumeTimeBasedScroll();
          }, 50);
        }
      });

    } catch (error) {
      console.error('[AutoScrollGallery] Failed to initialize Splide:', error);
      // Try fallback
      this.initBasicSplide();
    }
  }

  _startTimeBasedScroll() {
    this._timeScrollPaused = false;
    this._timeScrollLastTimestamp = null;

    const step = (timestamp) => {
      if (this._timeScrollPaused) {
        this._timeScrollLastTimestamp = null;
        this._timeScrollRafId = requestAnimationFrame(step);
        return;
      }

      if (this._timeScrollLastTimestamp === null) {
        this._timeScrollLastTimestamp = timestamp;
        this._timeScrollRafId = requestAnimationFrame(step);
        return;
      }

      const deltaMs = timestamp - this._timeScrollLastTimestamp;
      this._timeScrollLastTimestamp = timestamp;

      // Cap delta to avoid huge jumps when tab is backgrounded
      const cappedDelta = Math.min(deltaMs, 100);
      const pxToMove = (this._timeScrollPxPerSec * cappedDelta) / 1000;

      if (this.splide && this.splide.Components && this.splide.Components.Move) {
        const currentPos = this.splide.Components.Move.getPosition();
        this.splide.Components.Move.translate(currentPos - pxToMove);
      }

      this._timeScrollRafId = requestAnimationFrame(step);
    };

    this._timeScrollRafId = requestAnimationFrame(step);
  }

  _pauseTimeBasedScroll() {
    this._timeScrollPaused = true;
  }

  _resumeTimeBasedScroll() {
    this._timeScrollPaused = false;
  }

  _stopTimeBasedScroll() {
    this._timeScrollPaused = true;
    if (this._timeScrollRafId) {
      cancelAnimationFrame(this._timeScrollRafId);
      this._timeScrollRafId = null;
    }
  }

  openDialog(clickedIndex = 0) {
    if (!this.dialog) {
      return;
    }
    // Pause the time-based scrolling
    this._pauseTimeBasedScroll();

    // Store current focus to restore later if needed
    const currentFocus = document.activeElement;
    
    // Show the dialog
    this.dialog.showModal();
    
    // Immediately blur any focused element to prevent Safari's default focus
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
    
    // Initialize fullscreen splide
    this.initFullscreenSplide(clickedIndex);
    
    // Only trap focus and set initial focus if opened by keyboard
    if (this._keyboardOpen) {
      setTimeout(() => {
        const paginationContainer = this.dialog.querySelector('.fullscreen-carousel__pagination');
        const firstPaginationButton = paginationContainer?.querySelector('.pagination-number');
        if (firstPaginationButton) {
          if (typeof trapFocus === 'function') {
            trapFocus(this.dialog, firstPaginationButton);
          } else {
            firstPaginationButton.focus();
          }
        } else {
          // fallback: trap focus on dialog, but don't focus close button
          if (typeof trapFocus === 'function') {
            trapFocus(this.dialog, this.dialog);
          }
        }
      }, 0);
    } else {
      // For mouse clicks, just ensure focus trap without initial focus
      if (typeof trapFocus === 'function') {
        trapFocus(this.dialog);
      }
    }
    
    if (typeof Shopify !== 'undefined' && Shopify.analytics) {
      Shopify.analytics.publish('image_gallery_opened');
    }
  }

  handleDialogFocusTrap(e) {
    if (e.key !== 'Tab') return;
    const dialog = e.currentTarget;
    const focusable = dialog.querySelectorAll('.fullscreen-carousel-dialog__close, .pagination-number');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  closeDialog(event) {
    if (!this.dialog) return;
    // Remove focus trap
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }
    this.dialog.close();
    // Return focus to trigger if opened by keyboard
    if (this._keyboardOpen && this._lastTrigger) {
      setTimeout(() => {
        this._lastTrigger.focus();
      }, 0);
    }
    // Resume the time-based scrolling
    this._resumeTimeBasedScroll();

    if (typeof Shopify !== 'undefined' && Shopify.analytics) {
      Shopify.analytics.publish('image_gallery_closed');
    }
  }

  handleDialogClose() {
    // Resume time-based scroll when dialog closes
    this._resumeTimeBasedScroll();
  }

  handleDialogClick(e) {
    // Don't close if clicking on pagination, navigation buttons, close button
    if (
      e.target.closest('.fullscreen-carousel__pagination') ||
      e.target.closest('.splide__pagination') ||
      e.target.closest('.fullscreen-carousel-dialog__close') ||
      e.target.closest('.splide__arrow')
    ) {
      e.stopPropagation();
      return;
    }

    // Close the dialog when clicking anywhere else
    this.closeDialog(e);
  }

  initFullscreenSplide(initialSlideIndex = 0) {
    const fullscreenCarousel = this.dialog.querySelector('.fullscreen-carousel');
    if (!fullscreenCarousel) {
      return;
    }

    // For fullscreen, we can use basic Splide even if AutoScroll isn't available
    if (typeof Splide === 'undefined') {
      return;
    }

    // Destroy existing splide if it exists
    if (this.fullscreenSplide) {
      this.fullscreenSplide.destroy();
    }

    // Different config for mobile vs desktop
    const config = {
      start: initialSlideIndex,
      pagination: true, // Enable built-in pagination
      arrows: false, // Disable arrows since we have pagination
      keyboard: true,
      drag: true,
      // No auto-scroll in fullscreen mode
    };

    if (this.isMobile) {
      // Mobile: disable carousel behavior for natural scrolling
      config.drag = 'free';
      config.wheel = true;
      config.keyboard = false;
      config.pagination = false; // Hide pagination on mobile
      config.type = 'slide';
      config.perPage = 1;
      config.direction = 'ttb';
      config.height = '100vh';
      config.snap = false; // Disable snapping for smoother scrolling
      config.flickPower = 0; // Disable flick behavior
      config.flickMaxPages = 0; // Prevent page jumping on flick
    } else {
      // Desktop: vertical carousel layout
      config.direction = 'ttb';
      config.height = '100vh';
      config.perPage = 1;
      config.keyboard = true; // Enable keyboard navigation
      config.wheel = true; // Re-enable built-in wheel handling
      config.releaseWheel = true;
      config.type = 'slide';
      config.pagination = false; // Disable built-in pagination for custom numbered version
      // Improve wheel sensitivity for trackpads
      config.wheelSleep = 300; // Longer delay between wheel events
      config.wheelMinThreshold = 20; // Higher threshold for trackpad inertia
      config.waitForTransition = true
    }

    try {
      this.fullscreenSplide = new Splide(fullscreenCarousel, config);

      // Track slide changes for analytics
      this.fullscreenSplide.on('moved', (newIndex) => {
        if (typeof Shopify !== 'undefined' && Shopify.analytics) {
          Shopify.analytics.publish('image_gallery_viewed', { 
            image_location: newIndex + 1 
          });
        }
      });

      // Set up custom numbered pagination after mounting
      this.fullscreenSplide.on('mounted', () => {
        this.setupNumberedPagination();
        // Set up arrow key navigation after splide is mounted
        this.setupModalArrowNavigation();
      });

      this.fullscreenSplide.mount();
      
    } catch (error) {
    }
  }

  setupNumberedPagination() {
    if (!this.fullscreenSplide) {
      console.warn('[AutoScrollGallery] No fullscreen splide for pagination');
      return;
    }

    const paginationContainer = this.dialog.querySelector('.fullscreen-carousel__pagination');
    if (!paginationContainer) {
      console.warn('[AutoScrollGallery] No pagination container found');
      return;
    }

    const totalSlides = this.fullscreenSplide.length;
    
    // Create numbered pagination HTML
    let paginationHTML = '';
    for (let i = 0; i < totalSlides; i++) {
      const formattedNumber = String(i + 1).padStart(2, '0');
      const activeClass = i === this.fullscreenSplide.index ? 'active' : '';
      paginationHTML += `<button class="pagination-number ${activeClass}" data-index="${i}" tabindex="0" aria-label="Go to slide ${i + 1}">${formattedNumber}</button>`;
    }
    paginationContainer.innerHTML = paginationHTML;

    // Add click events to pagination
    paginationContainer.addEventListener('click', (e) => {
      const target = e.target;
      if (target.classList.contains('pagination-number')) {
        const index = parseInt(target.dataset.index, 10);
        if (!isNaN(index) && this.fullscreenSplide) {
          this.fullscreenSplide.go(index);
        }
        e.stopPropagation();
      }
    });

    // Update active pagination on slide change
    this.fullscreenSplide.on('moved', (newIndex) => {
      const buttons = paginationContainer.querySelectorAll('.pagination-number');
      buttons.forEach((button, index) => {
        button.classList.toggle('active', index === newIndex);
        });
      });
  }
}

customElements.define('auto-scroll-gallery', AutoScrollGallery);